import { useMemo } from 'react';
import type { HistoryResponse, LeagueBundle, PlayersFile } from '../../shared/types';
import {
  Avatar,
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  PositionTag,
  Section,
  Stat,
} from '../components/common';
import { useLineups, useMatchups } from '../lib/api';
import { fmtPoints } from '../lib/format';
import {
  buildTeamViews,
  historyTeamIndex,
  lastLeagueWeek,
  lookupPlayer,
  teamIndex,
  type TeamView,
} from '../lib/league';
import { analyseAll } from '../lib/projections';
import { buildRecap, recapCoaching, type RecapSide } from '../lib/recap';
import { managerDirectory } from '../lib/rivalries';
import { Link } from '../lib/router';

function TeamLine({
  side,
  teams,
  linkable,
  suffix,
}: {
  side: RecapSide;
  teams: Map<number, TeamView>;
  linkable: boolean;
  suffix?: string;
}) {
  const t = teams.get(side.rosterId);
  const name = t?.name ?? `Roster ${side.rosterId}`;
  const inner = (
    <div className="team-cell">
      <Avatar id={t?.avatar ?? null} alt={name} />
      <div className="team-names">
        <div className="team-title">{name}</div>
        {suffix && <div className="team-handle">{suffix}</div>}
      </div>
    </div>
  );
  return linkable ? <Link to={`/teams/${side.rosterId}`}>{inner}</Link> : inner;
}

export default function WeekRecap({
  bundle,
  history,
  players,
  season,
  week,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
  players: PlayersFile | null;
  season: string;
  week: number;
}) {
  const isCurrent = season === bundle.league.season;
  const archived = (history?.seasons ?? []).find((s) => s.season === season) ?? null;
  const leagueId = isCurrent ? bundle.league.league_id : (archived?.league_id ?? null);

  const teams = useMemo(() => {
    if (isCurrent) return teamIndex(buildTeamViews(bundle));
    return archived ? historyTeamIndex(archived) : new Map<number, TeamView>();
  }, [isCurrent, bundle, archived]);

  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  // A hook cannot be called conditionally, so an unknown season falls back to
  // the current league id and the render guard below rejects it instead.
  const matchups = useMatchups(leagueId ?? bundle.league.league_id, week);
  const lineups = useLineups();

  const recap = useMemo(() => buildRecap(matchups.data), [matchups.data]);

  const coaching = useMemo(() => {
    const all = analyseAll(lineups.data);
    return recapCoaching(all.filter((w) => w.season === season && w.week === week));
  }, [lineups.data, season, week]);

  const lastWeek = lastLeagueWeek(bundle.league);

  if (leagueId === null || week < 1 || week > lastWeek) {
    return (
      <>
        <PageHead title="Week not found" meta={[`${season} · week ${week}`]} />
        <div className="card">
          <Empty>
            No such week in this league. <Link to="/schedule">The schedule</Link> has every week
            that exists.
          </Empty>
        </div>
      </>
    );
  }

  if (matchups.loading) return <Loading label={`Loading week ${week}`} />;
  if (matchups.error || !recap) {
    return <ErrorState message={matchups.error ?? 'No matchups returned'} onRetry={matchups.reload} />;
  }

  const prev = week > 1 ? week - 1 : null;
  const next = week < lastWeek ? week + 1 : null;
  const managerName = (userId: string) => dir.get(userId)?.name ?? 'Unknown';

  if (!recap.played) {
    return (
      <>
        <PageHead
          title={`Week ${week}`}
          meta={[`${season} season`, 'Not played yet']}
        />
        <div className="toolbar">
          {prev && (
            <Link to={`/recap/${season}/${prev}`} className="chip">
              ← Week {prev}
            </Link>
          )}
          <Link to="/schedule" className="chip">
            Schedule
          </Link>
          {next && (
            <Link to={`/recap/${season}/${next}`} className="chip">
              Week {next} →
            </Link>
          )}
        </div>
        <div className="card">
          <Empty>
            This week has not been played. The <Link to="/">home page</Link> projects the next one.
          </Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title={`Week ${week}`}
        meta={[
          `${season} season`,
          recap.isPlayoff && 'Playoffs',
          `${recap.games.filter((g) => g.margin !== null).length} games`,
          `Average ${fmtPoints(recap.leagueMean)}`,
        ]}
      />

      <div className="toolbar">
        {prev && (
          <Link to={`/recap/${season}/${prev}`} className="chip">
            ← Week {prev}
          </Link>
        )}
        <Link to="/schedule" className="chip">
          Schedule
        </Link>
        {next && (
          <Link to={`/recap/${season}/${next}`} className="chip">
            Week {next} →
          </Link>
        )}
      </div>

      <Section title="Results">
        <div className="grid grid-2 grid-start">
          {recap.games.map((game) => {
            const ordered = [...game.sides].sort((a, b) => b.points - a.points);
            return (
              <div className="card card-pad" key={game.matchupId}>
                {ordered.map((side) => (
                  <div
                    className={`score-row ${game.winner === side.rosterId ? 'score-win' : ''}`}
                    key={side.rosterId}
                  >
                    <TeamLine side={side} teams={teams} linkable={isCurrent} />
                    <span className="score-pts tabnum">{fmtPoints(side.points)}</span>
                  </div>
                ))}
                <div className="score-foot">
                  <span>
                    {game.margin === null
                      ? 'No opponent'
                      : game.winner === null
                        ? 'Tied'
                        : `Won by ${fmtPoints(game.margin)}`}
                  </span>
                  <span className="faint">{fmtPoints(game.combined)} combined</span>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="The week's edges">
        <div className="grid grid-4">
          <Stat
            label="High score"
            value={recap.highest ? fmtPoints(recap.highest.value.points) : '—'}
            note={
              recap.highest ? (teams.get(recap.highest.value.rosterId)?.name ?? '—') : undefined
            }
          />
          <Stat
            label="Low score"
            value={recap.lowest ? fmtPoints(recap.lowest.value.points) : '—'}
            note={recap.lowest ? (teams.get(recap.lowest.value.rosterId)?.name ?? '—') : undefined}
          />
          <Stat
            label="Biggest margin"
            value={recap.blowout?.margin != null ? fmtPoints(recap.blowout.margin) : '—'}
            note={
              recap.blowout?.winner != null
                ? (teams.get(recap.blowout.winner)?.name ?? '—')
                : undefined
            }
          />
          <Stat
            label="Closest game"
            value={recap.closest?.margin != null ? fmtPoints(recap.closest.margin) : '—'}
            note={
              recap.closest?.winner != null
                ? (teams.get(recap.closest.winner)?.name ?? '—')
                : undefined
            }
          />
        </div>
      </Section>

      <Section title="Top performers" hint="Starters only">
        <div className="card">
          {recap.topPlayers.map((p, i) => {
            const player = lookupPlayer(players, p.playerId);
            const t = teams.get(p.rosterId);
            return (
              <div className="player-row" key={`${p.playerId}-${i}`}>
                <span className="slot-label">{i + 1}</span>
                <PositionTag position={player.position} />
                <span className="player-name">{player.name}</span>
                <span className="player-meta">{t?.name ?? `Roster ${p.rosterId}`}</span>
                <span className="player-meta tabnum" style={{ color: 'var(--text)' }}>
                  {fmtPoints(p.points)}
                </span>
              </div>
            );
          })}
          {recap.topPlayers.length === 0 && <Empty>No scoring starters this week.</Empty>}
        </div>
      </Section>

      {coaching.weeks.length > 0 && (
        <Section title="Lineup decisions">
          <div className="grid grid-4">
            <Stat
              label="Best call"
              value={
                coaching.bestCall
                  ? `+${Math.abs(coaching.bestCall.coachingCost).toFixed(1)}`
                  : '—'
              }
              note={
                coaching.bestCall
                  ? managerName(coaching.bestCall.userId)
                  : 'nobody beat the projections'
              }
            />
            <Stat
              label="Costliest call"
              value={coaching.worstCall ? `−${coaching.worstCall.coachingCost.toFixed(1)}` : '—'}
              note={coaching.worstCall ? managerName(coaching.worstCall.userId) : undefined}
            />
            <Stat
              label="Nothing they could do"
              value={
                coaching.unluckiest ? coaching.unluckiest.unforeseeable.toFixed(1) : '—'
              }
              note={coaching.unluckiest ? managerName(coaching.unluckiest.userId) : undefined}
            />
            <Stat
              label="Most of the ceiling"
              value={
                coaching.mostEfficient
                  ? `${Math.round((coaching.mostEfficient.actual / coaching.mostEfficient.perfect) * 100)}%`
                  : '—'
              }
              note={coaching.mostEfficient ? managerName(coaching.mostEfficient.userId) : undefined}
            />
          </div>
          <Legend
            items={[
              { term: 'Best call', def: 'points gained over following the projections' },
              { term: 'Costliest call', def: 'points the projections would have gained' },
              { term: 'Nothing they could do', def: 'bench points no projection pointed at' },
              { term: 'Most of the ceiling', def: 'share of the perfect lineup actually scored' },
            ]}
          />
        </Section>
      )}

      {recap.benchLeader && recap.benchLeader.benchPoints > 0 && (
        <Section title="Left on the bench">
          <div className="card card-pad">
            <div className="score-row">
              <TeamLine side={recap.benchLeader} teams={teams} linkable={isCurrent} />
              <span className="score-pts tabnum">{fmtPoints(recap.benchLeader.benchPoints)}</span>
            </div>
          </div>
        </Section>
      )}
    </>
  );
}
